import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card } from '../components/ui/card';
import { Skeleton } from '../components/ui/skeleton';
import { ArrowLeft, Clock, Share2 } from 'lucide-react';
import '../styles/article.css';

function BlogDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBlog();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchBlog = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/blogs/${id}`);
      if (!response.ok) {
        throw new Error('Blog not found');
      }
      const data = await response.json();
      setArticle(data);
    } catch (error) {
      console.error('Failed to fetch blog:', error);
      navigate('/blog');
    } finally {
      setLoading(false);
    }
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: article.title,
          text: article.excerpt,
          url: window.location.href,
        });
      } catch (err) {
        console.log('Share cancelled');
      }
    } else {
      navigator.clipboard.writeText(window.location.href);
      alert('Link copied to clipboard!');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background pb-20 md:pb-8">
        <div className="container mx-auto px-4 py-6 max-w-3xl">
          <Skeleton className="h-8 w-full mb-4" />
          <Skeleton className="h-64 w-full mb-4" />
          <Skeleton className="h-6 w-full mb-2" />
          <Skeleton className="h-6 w-3/4 mb-2" />
        </div>
      </div>
    );
  }

  if (!article) {
    return null;
  }

  return (
    <div className="min-h-screen bg-background pb-20 md:pb-8">
      <Helmet>
        <title>{article?.title ? `${article.title} - Mothering Blog` : 'Mothering Blog'}</title>
        <meta name="description" content={article?.excerpt || article?.meta_description || 'Read our latest blog post'} />
      </Helmet>

      {/* Sticky Header */}
      <div className="sticky top-0 z-10 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b">
        <div className="container mx-auto px-4 py-3 max-w-3xl">
          <div className="flex items-center justify-between">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/blog')}
              className="gap-2"
              data-testid="back-button"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="hidden sm:inline">Back</span>
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleShare}
              className="gap-2"
              data-testid="share-button"
            >
              <Share2 className="w-4 h-4" />
              <span className="hidden sm:inline">Share</span>
            </Button>
          </div>
        </div>
      </div>

      {/* Article Content */}
      <article className="container mx-auto px-4 py-6 max-w-2xl">
        {/* Hero Image */}
        {article.featured_image && (
          <div className="aspect-[16/9] rounded-2xl overflow-hidden mb-8 bg-muted shadow-lg">
            <img
              src={article.featured_image}
              alt={article.title}
              className="w-full h-full object-cover"
            />
          </div>
        )}

        {/* Meta Info */}
        <div className="flex items-center gap-3 mb-5 flex-wrap">
          {article.category && (
            <Badge className="bg-cyan-600 text-xs px-3 py-1">
              {article.category.charAt(0).toUpperCase() + article.category.slice(1)}
            </Badge>
          )}
          {article.reading_time && (
            <span className="text-sm text-muted-foreground flex items-center gap-1.5">
              <Clock className="w-4 h-4" />
              {article.reading_time} min read
            </span>
          )}
        </div>

        {/* Title */}
        <h1 className="text-[32px] leading-[1.15] md:text-[42px] md:leading-[1.1] font-heading font-bold mb-6 tracking-tight text-balance">
          {article.title}
        </h1>

        {/* Excerpt */}
        {article.excerpt && (
          <p className="text-[19px] leading-[1.6] text-muted-foreground mb-8 font-normal">
            {article.excerpt}
          </p>
        )}

        {/* Author & Date */}
        <div className="flex items-center gap-4 pb-8 mb-10 border-b text-sm text-muted-foreground">
          <span className="font-medium">{article.author || 'Mothering Team'}</span>
          <span className="text-muted-foreground/60">•</span>
          {article.created_at && (
            <time dateTime={article.created_at}>
              {new Date(article.created_at).toLocaleDateString('en-US', {
                month: 'long',
                day: 'numeric',
                year: 'numeric',
              })}
            </time>
          )}
        </div>

        {/* Article Body */}
        <div
          className="article-content
            prose prose-slate max-w-none
            prose-headings:font-heading prose-headings:font-bold prose-headings:tracking-tight
            prose-h2:text-2xl prose-h2:mt-12 prose-h2:mb-6 prose-h2:scroll-mt-20
            prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-4
            prose-p:text-[17px] prose-p:leading-[1.8] prose-p:mb-6 prose-p:text-foreground/90
            prose-a:text-cyan-600 prose-a:font-medium prose-a:no-underline hover:prose-a:underline hover:prose-a:text-cyan-700
            prose-strong:text-foreground prose-strong:font-semibold
            prose-ul:my-6 prose-ul:space-y-3
            prose-ol:my-6 prose-ol:space-y-3
            prose-li:text-[17px] prose-li:leading-[1.7] prose-li:text-foreground/90
            prose-li:marker:text-cyan-600
            prose-blockquote:border-l-4 prose-blockquote:border-cyan-600 prose-blockquote:bg-cyan-50/50 
            prose-blockquote:py-4 prose-blockquote:px-6 prose-blockquote:my-6 prose-blockquote:italic
            prose-code:bg-slate-100 prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded prose-code:text-[15px]
            prose-img:rounded-xl prose-img:my-8 prose-img:shadow-md
            first:prose-p:text-lg first:prose-p:leading-relaxed first:prose-p:text-foreground"
          dangerouslySetInnerHTML={{ __html: article.content }}
          data-testid="article-content"
        />

        {/* Tags */}
        {article.tags && article.tags.length > 0 && (
          <div className="mt-12 pt-8 border-t">
            <h3 className="text-sm font-semibold text-muted-foreground mb-4 uppercase tracking-wide">
              Related Topics
            </h3>
            <div className="flex flex-wrap gap-2">
              {article.tags.map((tag, index) => (
                <Badge 
                  key={index} 
                  variant="outline" 
                  className="text-sm px-3 py-1.5 hover:bg-cyan-50 hover:border-cyan-300 transition-colors cursor-pointer"
                >
                  #{tag}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {/* CTA */}
        <Card className="mt-12 p-8 md:p-10 bg-gradient-to-br from-cyan-500 via-cyan-600 to-cyan-700 border-0 shadow-xl">
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-white/20 rounded-full mb-4">
              <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
              </svg>
            </div>
            <h3 className="text-2xl md:text-3xl font-bold text-white mb-3">
              Ready to Shop?
            </h3>
            <p className="text-cyan-50 mb-6 text-base max-w-md mx-auto">
              Discover quality products carefully curated for your little ones
            </p>
            <Button
              onClick={() => navigate('/products')}
              size="lg"
              className="bg-white text-cyan-600 hover:bg-cyan-50 shadow-lg hover:shadow-xl transition-all font-semibold"
              data-testid="shop-cta"
            >
              Browse Products
            </Button>
          </div>
        </Card>
      </article>

      {/* Bottom spacing for mobile nav */}
      <div className="h-20 md:hidden" />
    </div>
  );
}

export default BlogDetailPage;
